import { Request, Response } from '@konv/http';
import { UserStorage } from '../shared/tools/UserStorage';
import { Environment } from '../shared/core/Environment';

export class EnvironmentsAPI {
    static async request({
        endpoint = '/',
        method = 'GET',
        headers,
        query,
        data
    }) {
        return await Request.request({
            endpoint: endpoint,
            method: method,
            headers: headers,
            service: 'environments',
            ignoreVersion: true,
            params: query,
            data: data
        });
    }

    static async requestAuthenticated({
        endpoint = '/',
        method = 'GET',
        query,
        data
    }) {
        return await EnvironmentsAPI.request({
            endpoint,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() },
            method,
            query,
            data,
        });
    }

    static async getById(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}`,
            method: 'GET'
        });
    }

    static async getS3Configurations(environmentId) {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_CDN_ENVIRONMENT_CONFIGURATIONS}/${environmentId}.json?t=${Date.now()}`
            );
            return response.json();
        } catch (err) {
            return null;
        }
    }

    static async getUnits(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/units`,
            method: 'GET'
        });
    }

    static async getAvailableUnits(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/users/me/units/available`,
            method: 'GET'
        });
    }

    static async getAvailableUnitsByUser(environmentId, userId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/users/${userId}/units/available`,
            method: 'GET'
        });
    }

    static async postUnit(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}`,
            method: 'POST',
            data
        });
    }

    static async putUnit(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}`,
            method: 'PUT',
            data
        });
    }

    static async getUsers(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/users`,
            method: 'GET'
        });
    }

    static async postEnvironment(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}`,
            method: 'POST',
            data
        });
    }

    static async getUserByUserId(environmentId, userId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/users/${userId}`,
            method: 'GET'
        });
    }

    static async postUserByEnvironmentId(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            method: 'POST',
            endpoint: `/${environmentId}/users`,
            data,
        });
    }

    static async putUserByUserId(environmentId, userId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            method: 'PUT',
            endpoint: `/${environmentId}/users/${userId}`,
            data,
        });
    }

    static async putMetadataByEnvironmentId(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            method: 'PUT',
            endpoint: `/${environmentId}/configurations/metadata`,
            data,
        });
    }

    static async getMetadataByEnvironmentId(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            method: 'GET',
            endpoint: `/${environmentId}/configurations/metadata`,
        });
    }

    static async deleteUserByUserId(environmentId, userId) {
        return await EnvironmentsAPI.requestAuthenticated({
            method: 'DELETE',
            endpoint: `/${environmentId}/users/${userId}`
        });
    }

    static async getConfigurationsDeliveryRegions(environmentId) {
        return await EnvironmentsAPI.request({
            method: 'GET',
            endpoint: `/${environmentId}/configurations/delivery_regions`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() }
        });
    }

    static async putConfigurationsDeliveryRegionsByEnvironmentId(
        environmentId,
        data
    ) {
        return await EnvironmentsAPI.request({
            method: 'PUT',
            endpoint: `/${environmentId}/configurations/delivery_regions`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() },
            data
        });
    }

    static async getConfigurationsAutomaticOrderAccept(environmentId) {
        return await EnvironmentsAPI.request({
            method: 'GET',
            endpoint: `/${environmentId}/configurations/automatic_order_accept`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() }
        });
    }

    static async putConfigurationsAutomaticOrderAccept(environmentId, data) {
        return await EnvironmentsAPI.request({
            method: 'PUT',
            endpoint: `/${environmentId}/configurations/automatic_order_accept`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() },
            data: {
                enabled: data
            }
        });
    }

    static async getConfigurationsAddress(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/address`,
            method: 'GET'
        });
    }

    static async putConfigurationsAddressByEnvironmentId(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/address`,
            method: 'PUT',
            data
        });
    }

    static async getConfigurationsDeliveryTime(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/delivery_time`,
            method: 'GET'
        });
    }

    static async putConfigurationsDeliveryTimeByEnvironmentId(
        environmentId,
        data
    ) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/delivery_time`,
            method: 'PUT',
            data
        });
    }

    static async getConfigurationsWorkingTime(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/working_time`,
            method: 'GET'
        });
    }

    static async putConfigurationsWorkingTime(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/working_time`,
            method: 'PUT',
            data
        });
    }

    static async getConfigurationsSchedule(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/schedule`,
            method: 'GET'
        });
    }

    static async getTablesEnvironment(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/tables`,
            method: 'GET'
        });
    }

    static async putTablesEnvironment(environmentId, data, id) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/tables/${id}`,
            method: 'PUT',
            data
        });
    }

    static async createTablesEnvironment(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/tables`,
            method: 'POST',
            data
        });
    }

    static async putConfigurationsTables(environmentId, value) {
        return await EnvironmentsAPI.request({
            method: 'PUT',
            endpoint: `/${environmentId}/configurations/tables`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() },
            data: value
        });
    }

    static async putConfigurationsSchedule(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/schedule`,
            method: 'PUT',
            data
        });
    }

    static async getConfigurationsModuleTables(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/tables`,
            method: 'GET'
        });
    }

    static async upsertAppSections(environmentId, data) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/app_sections`,
            method: 'PUT',
            data
        });
    }

    static async getAppSections(environmentId) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/app_sections`,
            method: 'GET',
        });
    }

    // static async getUsers(token, environmentId) {
    //     return await Axios.request({
    //         url: baseUrl + '/environments/' + environmentId + '/users',
    //         method: 'GET',
    //         headers: { Authorization: `Bearer ${token}` }
    //     }).then(r => r.data.response)
    // }

    // static async getConfigurationsPaymentMethods(token, environmentId) {
    //     return await Axios.request({
    //         url: baseUrl + '/environments/' + environmentId + '/configurations/payment_methods',
    //         method: 'GET',
    //         headers: { Authorization: `Bearer ${token}` }
    //     }).then(r => r.data.response)
    // }

    static async getConfigurationsPaymentMethods(environmentId) {
        return (
            await EnvironmentsAPI.requestAuthenticated({
                endpoint: `/${environmentId}/configurations/payment_methods`,
                method: 'GET'
            })
        ).getData({});
    }

    // static async getConfigurationsWebhooks(token, environmentId) {
    //     return await Axios.request({
    //         url: baseUrl + '/environments/' + environmentId + '/configurations/webhooks',
    //         method: 'GET',
    //         headers: { Authorization: `Bearer ${token}` }
    //     }).then(r => r.data.response)
    // }

    // static async getConfigurationsAddress(token, environmentId) {
    //     return await Axios.request({
    //         url: baseUrl + '/environments/' + environmentId + '/configurations/address',
    //         method: 'GET',
    //         headers: { Authorization: `Bearer ${token}` }
    //     }).then(r => r.data.response)
    // }

    // // Rotas POST
    // static async postEnvironmentByEnvironmentId(token, environmentId, body) {
    //     return await await Axios.request({
    //         url: `${baseUrl}/environments/${environmentId}`,
    //         method: 'POST',
    //         headers: { Authorization: `Bearer ${token}` },
    //         data: body
    //     }).then(r => r.data.response)
    // }

    // // Rotas PUT

    // static async putConfigurationsWebhooksByEnvironmentId(token, environmentId, body) {
    //     return await await Axios.request({
    //         url: `${baseUrl}/environments/${environmentId}/configurations/webhooks`,
    //         method: 'PUT',
    //         headers: { Authorization: `Bearer ${token}` },
    //         data: body
    //     }).then(r => r.data.response)
    // }

    // static async putConfigurationsAddressByEnvironmentId(token, environmentId, body) {
    //     return await await Axios.request({
    //         url: `${baseUrl}/environments/${environmentId}/configurations/address`,
    //         method: 'PUT',
    //         headers: { Authorization: `Bearer ${token}` },
    //         data: body
    //     }).then(r => r.data.response)
    // }

    static async putConfigurationsPaymentMethodsByEnvironmentId(
        environmentId,
        body
    ) {
        return await EnvironmentsAPI.requestAuthenticated({
            endpoint: `/${environmentId}/configurations/payment_methods`,
            method: 'PUT',
            noVersion: true,
            loading: false,
            data: body
        });
    }

    // static async putConfigurationsWorkingTimeByEnvironmentId(token, environmentId, body) {
    //     return await await Axios.request({
    //         url: `${baseUrl}/environments/${environmentId}/configurations/working_time`,
    //         method: 'PUT',
    //         headers: { Authorization: `Bearer ${token}` },
    //         data: body
    //     }).then(r => r.data.response)
    // }

    // static async putEnvironmentByEnvironmentId(token, environmentId, body) {
    //     return await await Axios.request({
    //         url: `${baseUrl}/environments/${environmentId}`,
    //         method: 'PUT',
    //         headers: { Authorization: `Bearer ${token}` },
    //         data: body
    //     }).then(r => r.data.response)
    // }

    // static async deleteEnvironmentByEnvironmentId(token, environmentId) {
    //     return await await Axios.request({
    //         url: `${baseUrl}/environments/${environmentId}`,
    //         method: 'DELETE',
    //         headers: { Authorization: `Bearer ${token}` }
    //     }).then(r => r.data.response)
    // }

    // static async deleteUserById(token, environmentId, userId) {
    //     return await await Axios.request({
    //         url: `${baseUrl}/environments/${environmentId}/users/${userId}`,
    //         method: 'DELETE',
    //         headers: { Authorization: `Bearer ${token}` }
    //     }).then(r => r.data.response)
    // }

    static async getConfigurationsByAlias(
        environmentId,
        alias,
        defaultValue = {}
    ) {
        let endpoint = `/${environmentId}/configurations`;
        if (alias) endpoint += `/${alias}`;
        const response = await EnvironmentsAPI.requestAuthenticated({
            endpoint,
            method: 'GET'
        });
        return response.getData(defaultValue);
    }

    static async putConfigurationsByAlias(environmentId, alias, data) {
        return await EnvironmentsAPI.request({
            method: 'PUT',
            endpoint: `/${environmentId}/configurations/${alias}`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() },
            data
        });
    }

    static async getPermissionsByType(environmentId) {
        return await EnvironmentsAPI.request({
            method: 'GET',
            endpoint: `/${environmentId}/configurations/permissions`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() }
        });
    }

    static async getTokenIsValid() {
        return await EnvironmentsAPI.request({
            method: 'GET',
            endpoint: `/session/validate`,
            headers: { Authorization: 'Bearer ' + UserStorage.getAccessToken() }
        });
    }
}
